import { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon, SparklesIcon, ArrowPathIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { IUserModel, AssetType, ImageGenerationStatus, ImageAnalyzeMode } from '@packages/global';
import { modelApi } from '../../services/user/model.api';
import { imageGenerationApi } from '../../services/user/imageGeneration.api';
import { promptHelperApi } from '../../services/user/promptHelper.api';
import { useCoinStore } from '../../stores/coinStore';
import { useImageGenerationStore } from '../../stores/imageGenerationStore';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { AssetGenerateSettings } from './AssetGenerateSettings';
import { AssetGeneratePreview } from './AssetGeneratePreview';

export interface ImageGenerationResult {
  imageUrl: string;
  prompt: string;
  imageGenerationId: string;
  artStyle?: string;
  subjectDescription?: string;
}

interface AssetGenerateModalProps {
  isOpen: boolean;
  onClose: () => void;
  subjectType: AssetType;
  subjectName: string;
  subjectDescription?: string;
  initialPrompt?: string;
  artStyle?: string;
  existingImageUrl?: string;
  initialMode?: 'text' | 'image';
  onConfirm: (result: ImageGenerationResult) => void;
  onSuccess?: () => void;
}

export default function AssetGenerateModalV2({
  isOpen, onClose,
  subjectType, subjectName,
  subjectDescription: initialSubjectDescription,
  initialPrompt, artStyle: defaultArtStyle = '',
  existingImageUrl, initialMode = 'text',
  onConfirm, onSuccess,
}: AssetGenerateModalProps) {
  const generationMode = initialMode; // 由外部参数决定，不再提供 UI 切换
  const { t } = useTranslation(['projectSettings', 'common']);

  const [models, setModels] = useState<IUserModel[]>([]);
  const [selectedModelId, setSelectedModelId] = useState('');
  const [modelsLoading, setModelsLoading] = useState(false);
  const [artStyle, setArtStyle] = useState(defaultArtStyle);
  const [editedPrompt, setEditedPrompt] = useState('');
  const [editedDescription, setEditedDescription] = useState(initialSubjectDescription || '');
  const [uploadedImageUrls, setUploadedImageUrls] = useState<string[]>([]);
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [analyzingPrompt, setAnalyzingPrompt] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const { account } = useCoinStore();
  const addTask = useImageGenerationStore(s => s.addTask);
  const removeTask = useImageGenerationStore(s => s.removeTask);
  const currentTask = useImageGenerationStore(
    s => currentTaskId ? s.activeTasks.get(currentTaskId) ?? null : null
  );

  const selectedModel = models.find(m => m.id === selectedModelId);
  const balance = account?.balance ?? 0;
  const coinPrice = selectedModel?.coinPrice ? selectedModel.coinPrice : 0;
  const isFreeDirectReplace = generationMode === 'image' && subjectType === AssetType.SCENE && !artStyle.trim() && uploadedImageUrls.length === 1;
  const hasEnoughCoins = isFreeDirectReplace || balance >= coinPrice;
  const maxImageCount = selectedModel?.imageInputCount || 4;

  const getSubjectTypeLabel = () => {
    switch (subjectType) {
      case AssetType.CHARACTER: return t('asset.generateModalV2.subjectType.character');
      case AssetType.PROP: return t('asset.generateModalV2.subjectType.prop');
      case AssetType.SCENE: return t('asset.generateModalV2.subjectType.scene');
      default: return t('asset.generateModalV2.subjectType.default');
    }
  };

  const getSubjectSettingTitle = () => t('asset.generateModalV2.titleGenerate', { type: getSubjectTypeLabel() });

  const getRegenerateTitle = () => t('asset.generateModalV2.titleRegenerate', { type: getSubjectTypeLabel() });

  const getGenerationHint = () => {
    if (subjectType === AssetType.CHARACTER) return t('asset.generateModalV2.hintCharacter');
    if (subjectType === AssetType.PROP) return t('asset.generateModalV2.hintProp');
    return t('asset.generateModalV2.hintScene');
  };

  const getAnalyzeMode = (): ImageAnalyzeMode => {
    const map: Record<AssetType, ImageAnalyzeMode> = {
      [AssetType.CHARACTER]: ImageAnalyzeMode.CHARACTER,
      [AssetType.PROP]: ImageAnalyzeMode.PROP,
      [AssetType.SCENE]: ImageAnalyzeMode.SCENE,
    };
    return map[subjectType] ?? ImageAnalyzeMode.DEFAULT;
  };

  useEffect(() => {
    if (isOpen) {
      loadModels(generationMode);
      setArtStyle(defaultArtStyle);
      // image 模式下提示词默认为空，避免旧提示词与新图片不匹配
      setEditedPrompt(generationMode === 'image' ? '' : (initialPrompt || ''));
      setEditedDescription(initialSubjectDescription || '');
      setUploadedImageUrls([]);
      setCurrentTaskId(null);
      setShowPreview(false);
      setError(null);
    }
  }, [isOpen]);

  const loadModels = async (mode: 'text' | 'image') => {
    setModelsLoading(true);
    try {
      const type = mode === 'text' ? 'text2image' : 'image_edit';
      const data = await modelApi.getAllModels({ type });
      setModels(data);
      setSelectedModelId(data.length > 0 ? data[0].id : '');
    } catch (err: any) {
      toast.error(err.message || t('asset.generateModalV2.loadModelFailed'));
    } finally {
      setModelsLoading(false);
    }
  };

  const handleImageUpload = (url: string) => {
    if (uploadedImageUrls.length < maxImageCount) {
      setUploadedImageUrls(prev => [...prev, url]);
      toast.success(t('asset.generateModalV2.imageUploadSuccess'));
    } else {
      toast.error(t('asset.generateModalV2.maxImageLimit', { max: maxImageCount }));
    }
  };

  const removeUploadedImage = (index: number) => {
    setUploadedImageUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleAnalyzePrompt = async () => {
    if (uploadedImageUrls.length === 0) {
      toast.error(t('asset.generateModalV2.uploadRefFirst'));
      return;
    }
    setAnalyzingPrompt(true);
    try {
      const result = await promptHelperApi.analyzeImagePrompt(uploadedImageUrls[0], getAnalyzeMode());
      setEditedPrompt(result.prompt);
      toast.success(t('asset.generateModalV2.analyzeComplete'));
    } catch (err: any) {
      toast.error(err.message || t('asset.generateModalV2.analyzeFailed'));
    } finally {
      setAnalyzingPrompt(false);
    }
  };

  const validateBeforeGenerate = (): boolean => {
    if (!selectedModelId) {
      toast.error(t('asset.generateModalV2.selectModelRequired'));
      return false;
    }
    if (generationMode === 'image') {
      if (uploadedImageUrls.length === 0) {
        toast.error(t('asset.generateModalV2.uploadRefFirst'));
        return false;
      }
      if (!editedPrompt.trim()) {
        toast.error(t('asset.generateModalV2.promptRequired'));
        return false;
      }
    }
    return true;
  };

  const handleGenerate = async () => {
    if (!validateBeforeGenerate()) return;
    setShowPreview(true);
    setIsGenerating(true);
    setError(null);
    try {
      const task = await imageGenerationApi.createSubjectImageGeneration({
        subjectType, subjectName,
        subjectDescription: editedDescription || undefined,
        userPrompt: editedPrompt || undefined,
        artStyle: artStyle || undefined,
        modelId: selectedModelId,
      });
      setCurrentTaskId(task.id);
      addTask(task);
      toast.success(t('asset.generateModalV2.taskCreated'));
    } catch (err: any) {
      setIsGenerating(false);
      setShowPreview(false);
      const msg = err.response?.status === 403 && err.message?.includes('剧点')
        ? t('asset.generateModalV2.insufficientCoins', { balance, cost: coinPrice })
        : err.message || t('asset.generateModalV2.generateFailed');
      setError(msg);
      toast.error(err.message || t('asset.generateModalV2.taskCreateFailed'));
    }
  };

  const handleImageGenerate = async () => {
    if (!validateBeforeGenerate()) return;

    // 从图片生成 + 场景 + 没有输入风格 → 直接替换，不发起生成任务，计费 0 剧点
    if (subjectType === AssetType.SCENE && !artStyle.trim() && uploadedImageUrls.length === 1) {
      onConfirm({
        imageUrl: uploadedImageUrls[0],
        prompt: editedPrompt.trim(),
        imageGenerationId: '',
        artStyle: undefined,
        subjectDescription: editedDescription.trim() || undefined,
      });
      resetAndClose();
      onSuccess?.();
      return;
    }

    setShowPreview(true);
    setIsGenerating(true);
    setError(null);
    try {
      const task = await imageGenerationApi.createSubjectImageEdit({
        subjectType, subjectName,
        subjectDescription: editedDescription || undefined,
        userPrompt: editedPrompt || undefined,
        artStyle: artStyle || undefined,
        modelId: selectedModelId,
        referenceImageUrls: uploadedImageUrls,
      });
      setCurrentTaskId(task.id);
      addTask(task);
      toast.success(t('asset.generateModalV2.taskCreated'));
    } catch (err: any) {
      setIsGenerating(false);
      setShowPreview(false);
      const msg = err.response?.status === 403 && err.message?.includes('剧点')
        ? t('asset.generateModalV2.insufficientCoins', { balance, cost: coinPrice })
        : err.message || t('asset.generateModalV2.generateFailed');
      setError(msg);
      toast.error(err.message || t('asset.generateModalV2.taskCreateFailed'));
    }
  };

  const doGenerate = () => generationMode === 'text' ? handleGenerate() : handleImageGenerate();

  useEffect(() => {
    if (!currentTask) return;
    if (currentTask.status === ImageGenerationStatus.COMPLETED) {
      setIsGenerating(false);
      setError(null);
      toast.success(t('asset.generateModalV2.imageCompleted'));
    }
    if (currentTask.status === ImageGenerationStatus.FAILED) {
      setIsGenerating(false);
      setError(currentTask.resultMessage || t('asset.generateModalV2.generateFailed'));
      toast.error(currentTask.resultMessage || t('asset.generateModalV2.imageFailed'));
    }
  }, [currentTask?.status, currentTask?.resultMessage]);

  useEffect(() => {
    if (!isGenerating || !currentTaskId) return;
    const id = setTimeout(() => {
      setIsGenerating(false);
      setError(t('asset.generateModalV2.generateTimeout'));
      toast.error(t('asset.generateModalV2.generateTimeout'));
    }, 5 * 60 * 1000);
    return () => clearTimeout(id);
  }, [isGenerating, currentTaskId]);

  const handleConfirm = () => {
    if (currentTask?.resultImageUrl) {
      onConfirm({
        imageUrl: currentTask.resultImageUrl,
        prompt: editedPrompt.trim(),
        imageGenerationId: currentTask.id,
        artStyle: artStyle || undefined,
        subjectDescription: editedDescription.trim() || undefined,
      });
      resetAndClose();
      onSuccess?.();
    }
  };

  const handleCancel = async () => {
    if (currentTaskId && isGenerating) {
      try {
        await imageGenerationApi.cancelImageGeneration(currentTaskId);
        removeTask(currentTaskId);
        setCurrentTaskId(null);
        setIsGenerating(false);
        setShowPreview(false);
        toast.success(t('asset.generateModalV2.cancelled'));
      } catch { toast.error(t('asset.generateModalV2.cancelFailed')); }
    }
  };

  const resetAndClose = () => {
    if (currentTaskId) removeTask(currentTaskId);
    setArtStyle(defaultArtStyle);
    setEditedPrompt('');
    setEditedDescription('');
    setCurrentTaskId(null);
    setShowPreview(false);
    setError(null);
    setIsGenerating(false);
    onClose();
  };

  const handleClose = () => {
    if (isGenerating && !error) {
      toast.error(t('asset.generateModalV2.generatingWait'));
      return;
    }
    resetAndClose();
  };

  const renderActions = () => {
    if (isGenerating) {
      return (
        <button onClick={handleCancel}
          className="w-full px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-500 rounded-lg transition-colors text-sm">
          {t('asset.generateModalV2.cancelGenerate')}
        </button>
      );
    }

    if (currentTask?.resultImageUrl) {
      return (
        <div className="flex gap-2">
          <button onClick={handleClose}
            className="flex-1 px-3 py-2 bg-bg-elevated hover:bg-bg-surface text-gray-300 rounded-lg transition-colors text-sm cursor-pointer">
            {t('asset.generateModalV2.cancel')}
          </button>
          <button onClick={doGenerate} disabled={!hasEnoughCoins}
            className="flex-1 px-3 py-2 bg-bg-elevated hover:bg-bg-surface text-gray-300 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5 text-sm cursor-pointer">
            <ArrowPathIcon className="h-3.5 w-3.5" /> {t('asset.generateModalV2.regenerate')}
          </button>
          <button onClick={handleConfirm}
            className="flex-1 px-3 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors flex items-center justify-center gap-1.5 text-sm cursor-pointer">
            <CheckCircleIcon className="h-3.5 w-3.5" /> {t('asset.generateModalV2.confirm')}
          </button>
        </div>
      );
    }

    if (error && showPreview) {
      return (
        <div className="flex gap-2">
          <button onClick={handleClose}
            className="flex-1 px-3 py-2 bg-bg-elevated hover:bg-bg-surface text-gray-300 rounded-lg transition-colors text-sm cursor-pointer">
            {t('asset.generateModalV2.close')}
          </button>
          <button onClick={doGenerate} disabled={!hasEnoughCoins}
            className="flex-1 px-3 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5 text-sm cursor-pointer">
            <ArrowPathIcon className="h-3.5 w-3.5" /> {t('asset.generateModalV2.retry')}
          </button>
        </div>
      );
    }

    return (
      <div className="flex gap-2">
        <button onClick={handleClose}
          className="flex-1 px-3 py-2 bg-bg-elevated hover:bg-bg-surface text-gray-300 rounded-lg transition-colors text-sm cursor-pointer">
          {t('asset.generateModalV2.cancel')}
        </button>
        <button
          onClick={doGenerate}
          disabled={!selectedModelId || modelsLoading || !hasEnoughCoins}
          className="flex-1 px-3 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 text-sm"
        >
          <SparklesIcon className="h-3.5 w-3.5" />
          {t('asset.generateModalV2.startGenerate')}
          {selectedModel && coinPrice > 0 && !isFreeDirectReplace && (
            <span className={`text-xs ${hasEnoughCoins ? 'text-yellow-400' : 'text-red-400'}`}>
              {t('asset.generateModalV2.coins', { cost: coinPrice })}
            </span>
          )}
        </button>
      </div>
    );
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => { }}>
        <Transition.Child as={Fragment}
          enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/60" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child as={Fragment}
              enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
              leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-bg-surface border border-border-subtle p-5 shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title className="text-base font-semibold text-gray-50 flex items-center gap-2">
                    <SparklesIcon className="h-5 w-5 text-primary" />
                    {existingImageUrl ? getRegenerateTitle() : getSubjectSettingTitle()}
                    {generationMode === 'image' && (
                      <span className="text-xs text-gray-400 font-normal bg-bg-form border border-border-subtle px-2 py-0.5 rounded">
                        {t('asset.generateModalV2.refImageMode')}
                      </span>
                    )}
                  </Dialog.Title>
                  <button onClick={handleClose} className="p-1 text-gray-400 hover:text-gray-50 transition-colors">
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* 主体内容 */}
                <div className="flex gap-4 items-stretch">
                  <AssetGenerateSettings
                    mode={generationMode}
                    subjectType={subjectType}
                    subjectName={subjectName}
                    editedDescription={editedDescription}
                    editedPrompt={editedPrompt}
                    artStyle={artStyle}
                    uploadedImageUrls={uploadedImageUrls}
                    maxImageCount={maxImageCount}
                    isGenerating={isGenerating}
                    balance={balance}
                    coinPrice={coinPrice}
                    hasEnoughCoins={hasEnoughCoins}
                    analyzingPrompt={analyzingPrompt}
                    onDescriptionChange={setEditedDescription}
                    onPromptChange={setEditedPrompt}
                    onArtStyleChange={setArtStyle}
                    onImageUpload={handleImageUpload}
                    onRemoveImage={removeUploadedImage}
                    onAnalyzePrompt={handleAnalyzePrompt}
                  />
                  <AssetGeneratePreview
                    models={models}
                    selectedModelId={selectedModelId}
                    modelsLoading={modelsLoading}
                    isGenerating={isGenerating}
                    error={error}
                    showPreview={showPreview}
                    existingImageUrl={existingImageUrl}
                    currentTask={currentTask}
                    generationHint={getGenerationHint()}
                    onModelChange={setSelectedModelId}
                  />
                </div>

                {/* 底部操作 */}
                <div className="mt-4">
                  {renderActions()}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
